import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  Cube,
  MagnifyingGlass,
  PencilRuler,
  Plus,
  SquaresFour,
  Waves,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { StageBadge } from "@/components/StageBadge";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { newArea } from "@/lib/quoteAreas";

const FEATURES = [
  { icon: PencilRuler, label: "Draw", text: "Quick-draw runs, freehand shapes, bump-outs, seams, cutouts" },
  { icon: SquaresFour, label: "Slab layout", text: "Nest every piece on real slabs from stock" },
  { icon: Waves, label: "Vein match", text: "Slide pieces on the slab photo, preview the seam" },
  { icon: Cube, label: "3D view", text: "Walk around the counters with the slab texture on" },
];

export default function DesignStudioPicker() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/quotes")
      .then(({ data }) => setQuotes(data || []))
      .catch(() => setQuotes([]));
  }, []);

  const startNew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/quotes", {
        project_name: `Design ${new Date().toLocaleDateString()}`,
        areas: [{ ...newArea(1), name: "Kitchen" }],
      });
      navigate(`/quotes/${data.id}/design`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not start a design",
      );
      setBusy(false);
    }
  };

  const term = search.trim().toLowerCase();
  const list = (quotes || [])
    .filter(
      (q) =>
        !term ||
        [q.code, q.project_name, q.customer_name, q.slab_color]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(term)),
    )
    .slice(0, 60);
  const pieceCount = (q) =>
    (q.areas || []).reduce(
      (n, a) => n + (a.pieces || []).filter((p) => !p.panel_kind).length,
      0,
    );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Design Studio" />
      <main className="relative z-10 px-4 pb-24 pt-6" data-testid="design-picker">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-zinc-600">
              Countertop drawing &amp; quoting
            </div>
            <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
              Design Studio
            </h1>
            <p className="mt-2 text-sm text-zinc-500">
              A full-screen workspace for one quote: draw the counters, nest them on
              slabs, match the veins and show the customer in 3D — with the price
              updating as you go.
            </p>
          </div>
          <button
            type="button"
            data-testid="design-new"
            disabled={busy}
            onClick={startNew}
            className="flex h-12 items-center gap-2 rounded-full bg-indigo-500 px-6 text-sm font-semibold text-white shadow-[0_0_16px_-2px_rgba(109,93,246,0.35)] transition-all hover:bg-indigo-400 active:scale-[0.98] disabled:opacity-40"
          >
            <Plus size={16} weight="bold" /> {busy ? "Starting…" : "New design"}
          </button>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.label}
                className="rounded-xl border border-zinc-800 bg-zinc-900 p-4 transition-colors hover:border-zinc-700"
              >
                <Icon size={20} weight="bold" className="text-indigo-300" />
                <div className="mt-2 text-sm font-semibold text-zinc-100">{f.label}</div>
                <div className="mt-1 text-xs text-zinc-500">{f.text}</div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Open a quote in the studio
          </span>
          <div className="relative ml-auto w-full sm:w-80">
            <MagnifyingGlass
              size={15}
              weight="bold"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <input
              data-testid="design-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search code, project, customer, stone…"
              className="h-10 w-full rounded-full border border-zinc-800 bg-zinc-950 pl-9 pr-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-indigo-400"
            />
          </div>
        </div>

        {quotes === null ? (
          <p className="mt-4 text-sm text-zinc-500">Loading quotes…</p>
        ) : list.length === 0 ? (
          <div
            data-testid="design-empty"
            className="mt-4 rounded-xl border border-dashed border-zinc-800 p-10 text-center text-sm text-zinc-500"
          >
            No quotes match. Start a new design above.
          </div>
        ) : (
          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="design-list">
            {list.map((qt) => (
              <Link
                key={qt.id}
                to={`/quotes/${qt.id}/design`}
                data-testid={`design-open-${qt.id}`}
                className="group flex items-start gap-3 rounded-xl border border-zinc-800 bg-zinc-900 p-4 transition-all hover:border-indigo-400/50 hover:bg-zinc-800/70"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-data text-xs text-indigo-300">{qt.code}</span>
                    <StageBadge stage={qt.status} />
                  </div>
                  <div className="mt-1 truncate text-sm font-semibold text-zinc-100">
                    {qt.project_name || "Untitled"}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-zinc-500">
                    {qt.customer_name || "No customer"}
                    {qt.slab_color ? ` · ${qt.slab_color}` : ""}
                  </div>
                  <div className="mt-2 flex items-center gap-3 font-mono-data text-[11px] text-zinc-500">
                    <span>{pieceCount(qt)} pieces</span>
                    <span>{money(qt.totals?.total || 0)}</span>
                  </div>
                </div>
                <ArrowRight
                  size={16}
                  weight="bold"
                  className="mt-1 shrink-0 text-zinc-600 transition-transform group-hover:translate-x-0.5 group-hover:text-indigo-300"
                />
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
